import axiosOriginal from 'axios';
import * as endpoints from '../constants/endpoints';

let lastController;

const mapScreenForUI = (s) => ({
    ...s,
    size: s?.width && s?.height ? `${s.width}x${s.height}` : undefined,
    label: s?.alias || s?.external_id || s?.id,
    lat: s?.geo?.lat ?? null,
    lng: s?.geo?.lon ?? null,
    isSelected: false,
    // Canonical alias: API returns the INDOOR/OUTDOOR enum as screen_location.
    // screenType is what all client-side filter logic reads.
    screenType: s?.screen_location ?? s?.screenType ?? null,
});

const clean = (p) => {
    const o = {};
    Object.keys(p).forEach((k) => {
        if (p[k] != null && p[k] !== '') {
            o[k] = p[k];
        }
    });
    return o;
};

const chunkArray = (items = [], chunkSize = 500) => {
    const chunks = [];
    for (let index = 0; index < items.length; index += chunkSize) {
        chunks.push(items.slice(index, index + chunkSize));
    }
    return chunks;
};

export async function getScreensPage(filters = {}) {
    const params = clean({
        page: filters.page ?? 1,
        limit: filters.limit ?? 100,
        orderBy: filters.orderBy ?? 'name',
        order: filters.order ?? 'asc',
        q: filters.q,
        provider: filters.provider,
        providerId: filters.providerId,
        publisher: filters.publisher,
        publisherId: filters.publisherId,
        screenType: filters.screenType,
        enabled: filters.enabled,
        testing: filters.testing,
        currency: filters.currency,
        maxCpm: filters.maxCpm,
        userMargin: filters.userMargin,
        rotation: filters.rotation,
        slotDuration: filters.slotDuration,
        venueTypeId: filters.venueTypeId,
        selectedCountries: filters.selectedCountries,
        selectedRegions: filters.selectedRegions,
        selectedCities: filters.selectedCities,
        selectedAreas: filters.selectedAreas,
        poiFilters: filters.poiFilters,
        externalIds: filters.externalIds,
        restriction: filters.restriction,
    });
    if (lastController) {
        lastController.abort();
    }
    lastController = new AbortController();
    const { data } = await axiosOriginal.get(endpoints.screensFiltered, {
        params,
        signal: lastController.signal,
        headers: {
            'x-username': 'dsp-admin',
            'x-password': 'p1p2p3p1p2p3',
        },
    });

    const results = Array.isArray(data?.results) ? data.results : [];
    const availableVenueTypeIds = Array.isArray(data?.availableVenueTypeIds)
        ? Array.from(
              new Set(
                  data.availableVenueTypeIds
                      .map((value) =>
                          Number.isFinite(Number(value)) ? Number(value) : null
                      )
                      .filter((value) => value !== null)
              )
          )
        : [];
    const availableRotations = Array.isArray(data?.availableRotations)
        ? Array.from(
              new Set(
                  data.availableRotations
                      .map((value) => String(value ?? '').trim())
                      .filter(Boolean)
              )
          )
        : [];
    const availableSlotDurations = Array.isArray(data?.availableSlotDurations)
        ? Array.from(
              new Set(
                  data.availableSlotDurations
                      .map((value) =>
                          Number.isFinite(Number(value)) ? Number(value) : null
                      )
                      .filter((value) => value !== null)
              )
          )
        : [];
    const availablePublisherIds = Array.isArray(data?.availablePublisherIds)
        ? Array.from(
              new Set(
                  data.availablePublisherIds
                      .map((value) =>
                          Number.isFinite(Number(value)) ? Number(value) : null
                      )
                      .filter((value) => value !== null)
              )
          )
        : [];
    const availableVenueTypeCounts = Array.isArray(
        data?.availableVenueTypeCounts
    )
        ? data.availableVenueTypeCounts
              .map((item) => {
                  const id = Number(item?.id);
                  const count = Number(item?.count ?? 0);
                  if (!Number.isFinite(id) || !Number.isFinite(count)) {
                      return null;
                  }
                  return { id, count };
              })
              .filter(Boolean)
        : [];
    const availableRotationCounts = Array.isArray(data?.availableRotationCounts)
        ? data.availableRotationCounts
              .map((item) => {
                  const value = String(item?.value ?? '').trim();
                  const count = Number(item?.count ?? 0);
                  if (!value || !Number.isFinite(count)) {
                      return null;
                  }
                  return { value, count };
              })
              .filter(Boolean)
        : [];
    const availableSlotDurationCounts = Array.isArray(
        data?.availableSlotDurationCounts
    )
        ? data.availableSlotDurationCounts
              .map((item) => {
                  const value = Number(item?.value);
                  const count = Number(item?.count ?? 0);
                  if (!Number.isFinite(value) || !Number.isFinite(count)) {
                      return null;
                  }
                  return { value, count };
              })
              .filter(Boolean)
        : [];
    const availablePublisherCounts = Array.isArray(
        data?.availablePublisherCounts
    )
        ? data.availablePublisherCounts
              .map((item) => {
                  const id = Number(item?.id);
                  const count = Number(item?.count ?? 0);
                  if (!Number.isFinite(id) || !Number.isFinite(count)) {
                      return null;
                  }
                  const name =
                      typeof item?.name === 'string' && item.name.trim()
                          ? item.name
                          : null;
                  return { id, count, name };
              })
              .filter(Boolean)
        : [];

    return {
        total: data?.total ?? results.length,
        page: data?.page ?? params.page,
        limit: data?.limit ?? params.limit,
        results: results.map(mapScreenForUI),
        availableVenueTypeIds,
        availableRotations,
        availableSlotDurations,
        availablePublisherIds,
        availableVenueTypeCounts,
        availableRotationCounts,
        availableSlotDurationCounts,
        availablePublisherCounts,
    };
}

export async function getScreensByExternalIds(externalIds = []) {
    const uniqueIds = Array.from(
        new Set(
            externalIds
                .map((value) => String(value ?? '').trim())
                .filter(Boolean)
        )
    );

    if (uniqueIds.length === 0) {
        return [];
    }

    const requests = chunkArray(uniqueIds, 500).map(async (chunk) => {
        const { data } = await axiosOriginal.get(endpoints.screensFiltered, {
            params: clean({
                page: 1,
                limit: chunk.length,
                orderBy: 'name',
                order: 'asc',
                externalIds: chunk.join(','),
            }),
            headers: {
                'x-username': 'dsp-admin',
                'x-password': 'p1p2p3p1p2p3',
            },
        });

        const results = Array.isArray(data?.results) ? data.results : [];
        return results.map(mapScreenForUI);
    });

    const screensByChunk = await Promise.all(requests);
    return screensByChunk.flat();
}

const normalizeArrayPayload = (payload) => {
    if (!payload) {
        return [];
    }
    if (Array.isArray(payload)) {
        return payload;
    }
    if (Array.isArray(payload.results)) {
        return payload.results;
    }
    if (Array.isArray(payload.result)) {
        return payload.result;
    }
    if (Array.isArray(payload.data)) {
        return payload.data;
    }
    return [];
};

const normalizePublisher = (item) => {
    if (!item) {
        return null;
    }

    const id =
        item.id ??
        item.publisher_id ??
        item.publisherId ??
        item.value ??
        item.key;
    if (id == null) {
        return null;
    }

    const name =
        item.name ??
        item.label ??
        item.display_name ??
        item.displayName ??
        String(id);

    return {
        ...item,
        id,
        name,
        email: item.email ?? item.contact ?? item.emailAddress ?? null,
    };
};

export async function getPublishersList(params = {}) {
    const requestParams = clean({
        page: params.page,
        limit: params.limit,
        q: params.q,
        orderBy: params.orderBy,
        order: params.order,
    });

    const { data } = await axiosOriginal.get(endpoints.publishers, {
        params: requestParams,
        headers: {
            'x-username': 'dsp-admin',
            'x-password': 'p1p2p3p1p2p3',
        },
    });

    let results = normalizeArrayPayload(data)
        .map((item) => normalizePublisher(item))
        .filter(Boolean);

    results = [...new Set(results.map((r) => r.name))].map((name) =>
        results.find((r) => r.name === name)
    );

    return {
        total: data?.total ?? results.length,
        totalPages: data?.totalPages ?? null,
        page: data?.page ?? requestParams.page ?? 1,
        limit: data?.limit ?? requestParams.limit ?? results.length,
        results,
    };
}
